import React, { useMemo, useState } from 'react';
import { Box, Grid, CircularProgress, Alert, Typography, Button } from '@mui/material';
import {
  Person as PersonIcon,
  Work as WorkIcon,
  Phone as PhoneIcon,
  Group as FamilyIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  EventAvailable as EventAvailableIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useProfile } from '../../../hooks';
import { FORM_CONFIGS } from '../../../constants';
import { ProfileSummary, ProfileSection, ProfileInfoItem, MyModal, DynamicForm, ResetPasswordModal } from '../../../components';

const ProfileInfoTab = ({ userId = null }) => {
  const { t } = useTranslation();
  const { profileData, isLoading, isUpdating, updateProfile, fetchProfile } = useProfile({ 
    skipAutoLoad: !!userId 
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);

  React.useEffect(() => {
    if (userId) {
      fetchProfile(userId);
    }
  }, [userId, fetchProfile]);

  const profileConfig = useMemo(() => FORM_CONFIGS.profile, []);
  const titleOptions = profileConfig.fields.find(f => f.name === 'title')?.options || [];
  const roleOptions = profileConfig.fields.find(f => f.name === 'role')?.options || [];

  const getInitials = name => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleProfileUpdate = async formData => {
    try {
      const result = await updateProfile(formData);
      if (result.success) {
        setIsEditModalOpen(false);
      }
    } catch (error) {
      console.error('Profile update error:', error);
    }
  };

  const getRoleDisplayName = roleValue => {
    const role = roleOptions.find(r => r.value === roleValue);
    if (!role) return roleValue;
    if (role.labelKey) return t(role.labelKey);
    return role.label || roleValue;
  };

  const getTitleDisplayName = titleValue => {
    const title = titleOptions.find(t => t.value === titleValue);
    if (!title) return titleValue;
    if (title.labelKey) return t(title.labelKey);
    return title.label || titleValue;
  };

  const getRelationshipDisplayName = value => {
    const relationships = {
      anne: 'Anne',
      baba: 'Baba',
      es: 'Eş',
      kardes: 'Kardeş',
      cocuk: 'Çocuk',
    };
    return relationships[value] || value;
  };


  const formatHoursToDaysOnly = (hours) => {
    if (hours === null || hours === undefined) return '0 gün';
    
    const totalHours = parseFloat(hours);
    const isNegative = totalHours < 0;
    const absHours = Math.abs(totalHours);
    
    // Saatleri güne çevir ve yuvarla (0.5'ten büyükse yukarı, küçükse aşağı)
    const days = Math.round(absHours / 8);
    
    const sign = isNegative ? '-' : '';
    const daysText = days === 1 ? t('profile.fields.day', 'gün') : t('profile.fields.days', 'gün');
    
    return `${sign}${days} ${daysText}`;
  };

  const formatWorkDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return null;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('profile.errors.dataNotFound', 'Profil bilgileri yüklenemedi. Lütfen sayfayı yenileyin.')}
        </Alert>
      </Box>
    );
  }

  const formattedStartDate = formatWorkDate(profileData?.startDate);
  const userData = {
    ...profileData,
    startDateFormatted: profileData?.startDate
      ? `${formattedStartDate} tarihinde işe başladı`
      : 'İş başlangıç tarihi belirtilmemiş',
  };

  return (
    <Box>
      <ProfileSummary
        userData={userData}
        onEdit={userId ? undefined : () => setIsEditModalOpen(true)}
        getInitials={getInitials}
        getRoleDisplayName={getRoleDisplayName}
        getTitleDisplayName={getTitleDisplayName}
        t={t}
        loading={false}
      />
      <Grid container spacing={4}>
      <Grid size={{ xs: 12, md: 6 }}>
      <ProfileSection
            title={t('profile.sections.personalInfo')}
            icon={<PersonIcon />}
            color="#2196f3"
          >
            <ProfileInfoItem
              icon={<PersonIcon />}
              label={
                t('profile.fields.firstName') +
                ' ' +
                t('profile.fields.lastName')
              }
              value={`${userData.firstName} ${userData.lastName}`}
              color="#2196f3"
            />
            <ProfileInfoItem
              icon={<EmailIcon />}
              label={t('profile.fields.email')}
              value={userData.email}
              color="#4caf50"
            />
            {!userId && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  startIcon={<LockResetIcon />}
                  onClick={() => setIsResetPasswordModalOpen(true)}
                  sx={{
                    width: '100%',
                    textTransform: 'none',
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                >
                  {t('auth.resetPassword.title', 'Şifre Sıfırla')}
                </Button>
              </Box>
            )}
          </ProfileSection>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
        <ProfileSection
            title={t('profile.sections.workInfo')}
            icon={<WorkIcon />}
            color="#9c27b0"
          >
            <Box
              sx={{
                mb: 3,
                p: 3,
                borderRadius: 3,
                background: (userData.userAnnualLeaveBalance - userData.totalLeavesUsed) < 0 
                  ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
                  : 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                border: '2px solid',
                borderColor: (userData.userAnnualLeaveBalance - userData.totalLeavesUsed) < 0 ? '#f44336' : '#4caf50',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, position: 'relative', zIndex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    mr: 2,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  <EventAvailableIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mb: 0.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 1.2,
                      color: 'rgba(255,255,255,0.95)',
                      fontSize: '0.75rem',
                    }}
                  >
                    {t('profile.fields.annualLeaveBalance', 'Yıllık İzin Bakiyesi')}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: 'white',
                      fontSize: { xs: '1.75rem', sm: '2.25rem' },
                      lineHeight: 1.2,
                      textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                  >
                    {formatHoursToDaysOnly(userData.userAnnualLeaveBalance-userData.totalLeavesUsed)}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <ProfileInfoItem
              icon={<WorkIcon />}
              label={t('profile.fields.role')}
              value={getRoleDisplayName(userData.role)}
              color="#ff9800"
            />
            <ProfileInfoItem
              icon={<CalendarIcon />}
              label={t('profile.fields.startDate')}
              value={formatWorkDate(userData.startDate) || 'Belirtilmemiş'}
              color="#4caf50"
            />
            <ProfileInfoItem
              icon={<CalendarIcon />}
              label={t('profile.fields.endDate')}
              value={
                userData.endDate
                  ? formatWorkDate(userData.endDate)
                  : t('profile.status.active')
              }
              color={userData.endDate ? '#f44336' : '#4caf50'}
            />
            <ProfileInfoItem
              icon={<BusinessIcon />}
              label={t('profile.fields.title')}
              value={
                userData.titleName ||
                getTitleDisplayName(userData.title) ||
                'Belirtilmemiş'
              }
              color="#9c27b0"
            />
          </ProfileSection>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
        <ProfileSection
            title={t('profile.sections.contactInfo')}
            icon={<PhoneIcon />}
            color="#ff9800"
          >
            <ProfileInfoItem
              icon={<PhoneIcon />}
              label={t('profile.fields.companyPhone')}
              value={userData.companyPhone || 'Belirtilmemiş'}
              color="#ff9800"
            />
            <ProfileInfoItem
              icon={<PhoneIcon />}
              label={t('profile.fields.personalPhone')}
              value={userData.personalPhone || 'Belirtilmemiş'}
              color="#4caf50"
            />
            <ProfileInfoItem
              icon={<HomeIcon />}
              label={t('profile.fields.address')}
              value={userData.address || 'Belirtilmemiş'}
              color="#607d8b"
            />
          </ProfileSection>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
        <ProfileSection
            title={t('profile.sections.emergencyContact')}
            icon={<FamilyIcon />}
            color="#f44336"
          >
            <ProfileInfoItem
              icon={<PersonIcon />}
              label={t('profile.fields.emergencyContactName')}
              value={userData.emergencyContactName || 'Belirtilmemiş'}
              color="#f44336"
            />
            <ProfileInfoItem
              icon={<FamilyIcon />}
              label={t('profile.fields.relationshipDegree')}
              value={
                getRelationshipDisplayName(userData.relationshipDegree) ||
                'Belirtilmemiş'
              }
              color="#9c27b0"
            />
            <ProfileInfoItem
              icon={<PhoneIcon />}
              label={t('profile.fields.emergencyContactPhone')}
              value={userData.emergencyContactPhone || 'Belirtilmemiş'}
              color="#ff5722"
            />
          </ProfileSection>
        </Grid>
      </Grid>
      <MyModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title={t(profileConfig.title.edit)}
        maxWidth="md"
        disableBackdropClose
        disableEscapeKeyDown
      >
        <DynamicForm
          fields={profileConfig.fields}
          initialData={userData}
          onSubmit={handleProfileUpdate}
          onCancel={() => setIsEditModalOpen(false)}
          loading={isUpdating}
          submitText={t('profile.buttons.update', 'Profili Güncelle')}
          cancelText={t('form.buttons.cancel')}
        />
      </MyModal>
      <ResetPasswordModal
        open={isResetPasswordModalOpen}
        onClose={() => setIsResetPasswordModalOpen(false)}
      />
    </Box>
  );
};

export default ProfileInfoTab;
